import type {
	IChartingLibraryWidget,
	LibrarySymbolInfo,
	ResolutionString,
	SeriesType,
} from "@public/charting_library";

type SavableChartWidget = Pick<
	TradingView.IChartingLibraryWidget,
	"saveChartToServer"
>;
// ponytail: not a no-op delegate — the `overrides: object` param widens
// getChartOverrides()'s `volumePaneSize: "medium"` literal so it satisfies
// applyOverrides without a per-call-site cast. Keep it.
type ThemeableChartWidget = {
	applyOverrides(overrides: unknown): void;
};

export function saveChartState(widget: SavableChartWidget) {
	return new Promise<void>((resolve) => {
		try {
			widget.saveChartToServer(
				() => resolve(),
				(error) => {
					console.warn("[Chart] Failed to save chart state.", error);
					resolve();
				},
				{
					defaultChartName: "TC",
				},
			);
		} catch (error) {
			console.warn("[Chart] Failed to save chart state.", error);
			resolve();
		}
	});
}

export function applyChartTheme(
	widget: ThemeableChartWidget,
	overrides: object,
) {
	widget.applyOverrides(overrides);
}

export type ChartEventHandlers = {
	onIntervalChanged?: (interval: ResolutionString) => void;
	onChartTypeChanged?: (chartType: SeriesType) => void;
	onSymbolChanged?: (symbolInfo: LibrarySymbolInfo) => void;
	onAutoSaveNeeded?: () => void;
};

/**
 * Wires the supplied handlers to the widget's event streams and returns a
 * teardown function that unsubscribes them. Safe to call only after
 * `onChartReady` has fired (`activeChart()` requires the inner API). The
 * returned teardown swallows errors so it can run during widget removal
 * without throwing.
 */
export function attachChartEventHandlers(
	widget: IChartingLibraryWidget,
	handlers: ChartEventHandlers,
) {
	const activeChart = widget.activeChart();
	if (handlers.onIntervalChanged) {
		activeChart.onIntervalChanged().subscribe(null, handlers.onIntervalChanged);
	}
	if (handlers.onChartTypeChanged) {
		activeChart
			.onChartTypeChanged()
			.subscribe(null, handlers.onChartTypeChanged);
	}
	if (handlers.onSymbolChanged) {
		activeChart.onSymbolChanged().subscribe(null, handlers.onSymbolChanged);
	}
	if (handlers.onAutoSaveNeeded) {
		widget.subscribe("onAutoSaveNeeded", handlers.onAutoSaveNeeded);
	}

	return () => {
		try {
			const ac = widget.activeChart();
			if (handlers.onIntervalChanged) {
				ac.onIntervalChanged().unsubscribe(null, handlers.onIntervalChanged);
			}
			if (handlers.onChartTypeChanged) {
				ac.onChartTypeChanged().unsubscribe(null, handlers.onChartTypeChanged);
			}
			if (handlers.onSymbolChanged) {
				ac.onSymbolChanged().unsubscribe(null, handlers.onSymbolChanged);
			}
			if (handlers.onAutoSaveNeeded) {
				widget.unsubscribe("onAutoSaveNeeded", handlers.onAutoSaveNeeded);
			}
		} catch {
			// Widget teardown should continue even if subscriptions are already gone.
		}
	};
}
